import { useEffect, useState } from "react";
import api from "@/lib/api";
import ThreeAvatar from "@/components/ThreeAvatar";
import {
  AVATAR_BRANDS,
  APPAREL_BRANDS,
  PALA_BRANDS,
  SHOES_BRANDS,
} from "@/lib/avatar";
import { Sparkle, User } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const absLogo = (u) => {
  if (!u) return null;
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/")) return `${API_URL}${u}`;
  return u;
};

// Normalize a gear-catalog row to the shape consumers expect.
function normalizeCatalog(list) {
  return (Array.isArray(list) ? list : []).map((it) => ({
    id: it.id,
    name: it.name,
    color: it.color || it.primary || "#0A0A0A",
    text: it.text || it.accent || "#FFFFFF",
    short: it.short || (it.name || "").slice(0, 3).toUpperCase(),
    logo: it.logo || null,
  }));
}

// Fallback used only when the backend catalog is unreachable (null).
const _FALLBACK = { apparel: APPAREL_BRANDS, pala: PALA_BRANDS, shoes: SHOES_BRANDS, avatar_brand: AVATAR_BRANDS };

/**
 * Read-only public view of another user's 3D avatar (with their equipment
 * brand logos legibly rendered on jersey / shorts / shoes / pala / cap).
 * Any authenticated user can see this via /profile/<userId> or embedded
 * inside PlayerProfile.
 */
export default function PublicAvatar3D({ userId, height = 420, showHeader = true, testId = "public-avatar-3d" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dyn, setDyn] = useState({ pala: null, apparel: null, shoes: null, avatar_brand: null });

  useEffect(() => {
    const reload = () => api.get("/gear-catalog").then(({ data }) => {
      setDyn({
        pala: data?.pala || [],
        apparel: data?.apparel || [],
        shoes: data?.shoes || [],
        avatar_brand: data?.avatar_brand || [],
      });
    }).catch(() => setDyn({ pala: [], apparel: [], shoes: [], avatar_brand: [] }));
    reload();
    window.addEventListener("gear-catalog:changed", reload);
    return () => window.removeEventListener("gear-catalog:changed", reload);
  }, []);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.get(`/users/${userId}/avatar-public`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) {
    return (
      <div className="card-tactical p-6 flex items-center justify-center" style={{ minHeight: height }} data-testid={`${testId}-loading`}>
        <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const a = data.avatar3d || {};
  const _resolve = (key) => normalizeCatalog(dyn[key] === null ? _FALLBACK[key] : dyn[key]);
  const clubBrandsAll = _resolve("avatar_brand");
  const palaBrandsAll = _resolve("pala");
  const apparelBrandsAll = _resolve("apparel");
  const shoesBrandsAll = _resolve("shoes");
  const clubBrand = clubBrandsAll.find((b) => b.id === (a.clubBrand || data.avatar_brand)) || clubBrandsAll[0];
  const palaBrand = palaBrandsAll.find((b) => b.id === (a.palaBrand || data.gear_pala_brand)) || null;
  const apparelBrand = apparelBrandsAll.find((b) => b.id === (a.apparelBrand || data.gear_apparel_brand)) || null;
  const shoesBrand = shoesBrandsAll.find((b) => b.id === (a.shoesBrand || data.gear_shoes_brand)) || null;
  const clubLogoUrl = absLogo(clubBrand?.logo);
  const palaLogoUrl = absLogo(palaBrand?.logo);
  const chestLogoUrl = absLogo(apparelBrand?.logo) || clubLogoUrl || null;
  const shortLogoUrl = absLogo(apparelBrand?.logo);
  const shoesLogoUrl = absLogo(shoesBrand?.logo);
  const capLogoUrl = clubLogoUrl;

  return (
    <div className="card-tactical p-4" data-testid={testId}>
      {showHeader && (
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#CCFF00] flex items-center gap-1">
              <Sparkle size={12} weight="fill" /> AR 3D · Avatar público
            </p>
            <h3 className="font-['Barlow_Condensed'] text-xl md:text-2xl font-black uppercase tracking-tight flex items-center gap-2">
              <User size={18} /> {data.name}
            </h3>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-brands`}>
            {apparelBrand && (
              <span className="px-2 py-1 rounded border border-white/10 bg-white/5 text-[10px] uppercase font-bold text-white tracking-wider flex items-center gap-1.5" title="Camiseta">
                {absLogo(apparelBrand.logo)
                  ? <img src={absLogo(apparelBrand.logo)} alt="" className="w-4 h-4 object-contain rounded-sm bg-white" />
                  : <span>👕</span>}
                {apparelBrand.name}
              </span>
            )}
            {palaBrand && (
              <span className="px-2 py-1 rounded border border-white/10 bg-white/5 text-[10px] uppercase font-bold text-white tracking-wider flex items-center gap-1.5" title="Pala">
                {absLogo(palaBrand.logo)
                  ? <img src={absLogo(palaBrand.logo)} alt="" className="w-4 h-4 object-contain rounded-sm bg-white" />
                  : <span>🏓</span>}
                {palaBrand.name}
              </span>
            )}
            {shoesBrand && (
              <span className="px-2 py-1 rounded border border-white/10 bg-white/5 text-[10px] uppercase font-bold text-white tracking-wider flex items-center gap-1.5" title="Calzado">
                {absLogo(shoesBrand.logo)
                  ? <img src={absLogo(shoesBrand.logo)} alt="" className="w-4 h-4 object-contain rounded-sm bg-white" />
                  : <span>👟</span>}
                {shoesBrand.name}
              </span>
            )}
            {clubBrand && (
              <span className="px-2 py-1 rounded border border-[#CCFF00]/30 bg-[#CCFF00]/10 text-[10px] uppercase font-bold text-[#CCFF00] tracking-wider flex items-center gap-1.5" title="Club / Sponsor">
                {absLogo(clubBrand.logo)
                  ? <img src={absLogo(clubBrand.logo)} alt="" className="w-4 h-4 object-contain rounded-sm bg-white" />
                  : <span>⭐</span>}
                {clubBrand.name}
              </span>
            )}
          </div>
        </div>
      )}
      <ThreeAvatar
        brandColors={{
          jersey: a.jersey || "#CCFF00",
          pants: a.pants || "#0A0A0A",
          shoes: a.shoes || "#E63946",
          pala: a.pala || "#00A0FF",
          skin: a.skin || "#E8C39E",
        }}
        outfit={{
          apparel: a.apparel || "short",
          shoes: a.shoesType || "low",
          headwear: a.headwear || "none",
        }}
        clubLogoUrl={clubLogoUrl}
        palaLogoUrl={palaLogoUrl}
        chestLogoUrl={chestLogoUrl}
        shortLogoUrl={shortLogoUrl}
        shoesLogoUrl={shoesLogoUrl}
        capLogoUrl={capLogoUrl}
        animation="idle"
        cameraMode="cinema"
        facePhotoUrl={a.facePhoto || null}
        height={height}
      />
    </div>
  );
}
